
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { IconProvider } from '@/components/ui';
import StructureSidebarLinks from './StructureSidebar-Links';
import StructureSidebarHistory from './StructureSidebar-History';
import StructureSidebarHighlights from './StructureSidebar-Highlights';
import StructureSidebarLenses from './StructureSidebarLenses';
import StructureSidebarBasicInfo from './StructureSidebar-BasicInfo';
import StructureSidebarDataInfo from './StructureSidebar-DataInfo';
import StructureSidebarDialogSection from './StructureSidebar-DialogSection';
import StructureSidebarNodeInfo from './StructureSidebar-NodeInfo';
import { GraphNavigation } from './GraphNavigation';
import { PanelRight, PanelLeft, Sparkles, Wand2, Eye, Info, Code, MessageSquare, Box } from 'lucide-react';
import { useDialogSectionOptional } from '@/contexts/DialogSectionContext';
import { useDialogNodeContextOptional } from '@/contexts/DialogNodeContext';


type menustateOptions = "basicInfo" | "links" | "history" | "highlights" | "lenses" | "dataInfo" | "dialogSection" | "nodeInfo";

interface StructureSidebarProps {
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    currentPath?: string; // Pass pathname as prop to avoid render loops
}

export default function StructureSidebar({ isCollapsed = true, onToggleCollapse, currentPath }: StructureSidebarProps = {}) {
    const [menustate, setMenustate] = React.useState<menustateOptions>("basicInfo");
    // Only call useLocation if currentPath not provided (for backward compatibility)
    const location = useLocation();
    const pathname = currentPath ?? location.pathname;
    const dialogSectionContext = useDialogSectionOptional();
    const dialogNodeContext = useDialogNodeContextOptional();

    // Get current view mode from context
    const viewMode = dialogSectionContext?.viewMode || null;

    // If view mode changes to edit and dialog section tab is active, switch to basic info
    React.useEffect(() => {
        if (viewMode === 'edit' && menustate === 'dialogSection') {
            setMenustate('basicInfo');
        }
    }, [viewMode, menustate]);

    // Determine if we're in documents (show Wand2) or templates/library (show link icon)
    const isInDocuments = pathname.startsWith('/documents/');
    
    // Determine if we're in templates (show Lenses tab)
    const isInTemplates = pathname.startsWith('/templates/');
    
    // Determine if we're in dialogs (show Dialog Section tab)
    const isInDialogs = pathname.startsWith('/dialogs/');

    // Listen for node clicks in flow mode and auto-switch to dialog section tab
    React.useEffect(() => {
        const handleNodeClickInFlow = () => {
            // Only switch if we're in dialogs, not in edit mode, and not collapsed
            const isDialogsPage = pathname.startsWith('/dialogs/');
            if (isDialogsPage && dialogSectionContext && viewMode !== 'edit' && !isCollapsed) {
                setMenustate("dialogSection");
            }
        };

        window.addEventListener('dialog-node-clicked-in-flow', handleNodeClickInFlow);
        return () => {
            window.removeEventListener('dialog-node-clicked-in-flow', handleNodeClickInFlow);
        };
    }, [pathname, dialogSectionContext, viewMode, isCollapsed]);

    const onSelectMenuItem = (item: menustateOptions) => {
        setMenustate(item);
    }

    const toggleSidebar = () => {
        if (onToggleCollapse) {
            onToggleCollapse();
        }
    }

    // When collapsed, show only the button in the upper corner (right in LTR, left in RTL)
    if (isCollapsed) {
        return (
            <div className="fixed top-0 end-0 z-50 p-1">
                <Button
                    variant="icon"
                    size="icon"
                    onClick={toggleSidebar}
                >
                    <PanelLeft className="h-4 w-4" />
                </Button>
            </div>
        );
    }

    return (
        <div className="w-full border-s border-border bg-muted flex flex-col h-full">
            <div className='h-12 bg-card border-border border-b flex items-center gap-2 px-2'>
                <Button variant="icon" size='icon' onClick={toggleSidebar}>
                    <PanelRight className="h-4 w-4" />
                </Button>
                {isInDialogs && dialogNodeContext && (
                    <Button 
                        variant="icon" 
                        size='icon' 
                        onClick={() => onSelectMenuItem("nodeInfo")}
                        disabled={viewMode !== 'flow'}
                        title={viewMode !== 'flow' ? 'Only available in flow mode' : 'Node Info'}
                    >
                        <Box className="h-4 w-4" />
                    </Button>
                )}
                <Button variant="icon" size='icon' onClick={() => onSelectMenuItem("basicInfo")}>
                    <Info className="h-4 w-4" />
                </Button>
                
                <Button variant="icon" size='icon' onClick={() => onSelectMenuItem("dataInfo")}>
                    <Code className="h-4 w-4" />
                </Button>

                {isInDialogs && dialogSectionContext && viewMode !== 'edit' && (
                    <Button variant="icon" size='icon' onClick={() => onSelectMenuItem("dialogSection")}>
                        <MessageSquare className="h-4 w-4" />
                    </Button>
                )}

             

                <Button variant="icon" size='icon' onClick={() => onSelectMenuItem("highlights")}>
                    {isInDocuments ? (
                        <Wand2 className="h-4 w-4" />
                    ) : (
                        <IconProvider iconstring="fa-link" style="light" size={20} />
                    )}
                </Button>

                {isInTemplates && (
                    <Button variant="icon" size='icon' onClick={() => onSelectMenuItem("lenses")}>
                        <Eye className="h-4 w-4" />
                    </Button>
                )}

                <Button variant="icon" size='icon' onClick={() => onSelectMenuItem("history")}>
                    <IconProvider iconstring="fa-history" style="light" size={16} />
                </Button>
            </div>

            <div className="flex-1 h-80 overflow-y-auto">
                {menustate === "basicInfo" && <StructureSidebarBasicInfo />}
                {menustate === "dataInfo" && <StructureSidebarDataInfo />}
                {menustate === "dialogSection" && dialogSectionContext && viewMode !== 'edit' && (
                    <StructureSidebarDialogSection
                        currentSectionIndex={dialogSectionContext.currentSectionIndex}
                        onSectionUpdate={dialogSectionContext.onSectionUpdate}
                    />
                )}
                {menustate === "nodeInfo" && dialogNodeContext && (
                    <StructureSidebarNodeInfo
                        onNodeUpdate={dialogNodeContext.onNodeUpdate || (() => {})}
                        availableNodes={dialogNodeContext.availableNodes}
                        availableDialogs={dialogNodeContext.availableDialogs}
                    />
                )}
                {menustate === "links" && <StructureSidebarLinks />}
                {menustate === "highlights" && <StructureSidebarHighlights />}
                {menustate === "lenses" && <StructureSidebarLenses />}
                {menustate === "history" && <StructureSidebarHistory />}
            </div>

            {!isInDialogs && <GraphNavigation />}
        </div>
    );

}